import { useState, useEffect } from 'react';
import { Shield, ShieldOff, ShieldAlert, Globe, Lock, Unlock, Settings, MapPin, Ban, AlertTriangle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { adminFetch } from '../../utils/adminFetch';
import DraggableModal from '../../components/ui/DraggableModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminSecurity = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [maintenance, setMaintenance] = useState(null);
  const [maintenanceForm, setMaintenanceForm] = useState({ message: '', hours: 1 });
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [blockForm, setBlockForm] = useState({ ip: '', reason: '', permanent: false });
  const [threshold, setThreshold] = useState(3);
  const [filter, setFilter] = useState('all');

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [secRes, maintRes, settRes] = await Promise.all([
        adminFetch(`${API}/admin/security`),
        adminFetch(`${API}/maintenance`),
        adminFetch(`${API}/admin/security/settings`)
      ]);
      setStats(await secRes.json());
      setMaintenance(await maintRes.json());
      const sett = await settRes.json();
      setThreshold(sett.auto_permanent_threshold || 3);
    } catch (e) {
      toast.error('Error al cargar datos de seguridad');
    } finally {
      setLoading(false);
    }
  };

  const handleUnblock = async (ip) => {
    try {
      await adminFetch(`${API}/admin/security/unblock-ip`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ip })
      });
      toast.success(`IP ${ip} desbloqueada`);
      fetchAll();
    } catch (e) { toast.error('Error'); }
  };

  const handlePermanentBlock = async (ip) => {
    try {
      await adminFetch(`${API}/admin/security/permanent-block-ip`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ip, reason: 'Bloqueado permanentemente (manual)' })
      });
      toast.success(`IP ${ip} bloqueada permanentemente`);
      fetchAll();
    } catch (e) { toast.error('Error'); }
  };

  const handleBlockIp = async () => {
    if (!blockForm.ip) return;
    const endpoint = blockForm.permanent ? 'permanent-block-ip' : 'block-ip';
    try {
      await adminFetch(`${API}/admin/security/${endpoint}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ip: blockForm.ip, reason: blockForm.reason || 'Bloqueado manualmente' })
      });
      toast.success(`IP ${blockForm.ip} bloqueada`);
      setShowBlockModal(false);
      setBlockForm({ ip: '', reason: '', permanent: false });
      fetchAll();
    } catch (e) { toast.error('Error'); }
  };

  const handleSaveSettings = async () => {
    try {
      await adminFetch(`${API}/admin/security/settings`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ auto_permanent_threshold: threshold })
      });
      toast.success('Ajustes guardados');
      setShowSettingsModal(false);
      fetchAll();
    } catch (e) { toast.error('Error'); }
  };

  const handleMaintenanceToggle = async () => {
    const enabling = !maintenance?.enabled;
    try {
      await adminFetch(`${API}/admin/maintenance`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          enabled: enabling,
          message: enabling ? maintenanceForm.message : null,
          hours: enabling ? maintenanceForm.hours : null
        })
      });
      toast.success(enabling ? 'Mantenimiento activado' : 'Mantenimiento desactivado');
      fetchAll();
    } catch (e) { toast.error('Error'); }
  };

  const blockedList = stats?.blocked_ip_list || [];
  const filteredList = filter === 'all' ? blockedList
    : filter === 'permanent' ? blockedList.filter(b => b.permanent)
    : blockedList.filter(b => !b.permanent);

  if (loading) return <div className="p-6 space-y-4">{[1,2,3].map(i => <div key={i} className="h-20 bg-muted animate-pulse" />)}</div>;

  return (
    <div className="p-6 space-y-6" data-testid="admin-security">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Seguridad</h1>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowSettingsModal(true)} data-testid="settings-btn">
            <Settings className="w-4 h-4 mr-1" /> Ajustes
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowBlockModal(true)} data-testid="block-ip-btn">
            <Ban className="w-4 h-4 mr-1" /> Bloquear IP
          </Button>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-card border border-border p-4">
          <div className="flex items-center gap-2 text-muted-foreground mb-1"><ShieldOff className="w-4 h-4" /> IPs Bloqueadas</div>
          <p className="text-2xl font-bold">{stats?.blocked_ips || 0}</p>
        </div>
        <div className="bg-card border border-red-500/30 p-4">
          <div className="flex items-center gap-2 text-red-500 mb-1"><Ban className="w-4 h-4" /> Permanentes</div>
          <p className="text-2xl font-bold text-red-500">{stats?.permanent_blocked || 0}</p>
        </div>
        <div className="bg-card border border-border p-4">
          <div className="flex items-center gap-2 text-muted-foreground mb-1"><ShieldAlert className="w-4 h-4" /> Cuentas con fallos</div>
          <p className="text-2xl font-bold">{stats?.accounts_with_failed_attempts || 0}</p>
        </div>
        <div className="bg-card border border-border p-4">
          <div className="flex items-center gap-2 text-muted-foreground mb-1"><Settings className="w-4 h-4" /> Umbral auto-perm.</div>
          <p className="text-2xl font-bold">{stats?.auto_permanent_threshold || 3} bloqueos</p>
        </div>
      </div>

      {/* Blocked IPs filter + list */}
      <div>
        <div className="flex items-center gap-3 mb-3">
          <h2 className="font-semibold">IPs Bloqueadas</h2>
          <div className="flex gap-1">
            {['all', 'permanent', 'temporary'].map(f => (
              <button key={f} onClick={() => setFilter(f)}
                className={`px-2 py-0.5 text-xs border transition-colors ${filter === f ? 'border-foreground bg-foreground text-background' : 'border-border text-muted-foreground'}`}>
                {f === 'all' ? `Todas (${blockedList.length})` : f === 'permanent' ? `Permanentes (${blockedList.filter(b=>b.permanent).length})` : `Temporales (${blockedList.filter(b=>!b.permanent).length})`}
              </button>
            ))}
          </div>
        </div>

        {filteredList.length === 0 ? (
          <div className="bg-card border border-border p-8 text-center text-muted-foreground">
            <Shield className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p>No hay IPs bloqueadas</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredList.map((b, i) => (
              <div key={i} className={`bg-card border p-3 ${b.permanent ? 'border-red-500/50' : 'border-yellow-500/50'}`}
                   data-testid={`blocked-ip-${b.ip}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <code className="text-sm font-mono font-bold">{b.ip}</code>
                      {b.permanent ? (
                        <span className="px-2 py-0.5 text-[10px] font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 flex items-center gap-1">
                          <Ban className="w-3 h-3" /> PERMANENTE
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 text-[10px] font-medium bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" /> TEMPORAL
                        </span>
                      )}
                      <span className="text-xs text-muted-foreground">({b.block_count} bloqueo{b.block_count > 1 ? 's' : ''})</span>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground flex-wrap">
                      {b.geo?.country && b.geo.country !== 'Desconocido' && (
                        <span className="flex items-center gap-1"><Globe className="w-3 h-3" /> {b.geo.city}, {b.geo.country}</span>
                      )}
                      {b.geo?.isp && b.geo.isp !== '-' && (
                        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {b.geo.isp}</span>
                      )}
                      <span>{b.reason}</span>
                    </div>
                    {!b.permanent && b.until && (
                      <p className="text-xs text-yellow-600 mt-1">
                        Expira: {new Date(b.until).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' })}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-1 ml-2">
                    {!b.permanent && (
                      <Button size="sm" variant="ghost" onClick={() => handlePermanentBlock(b.ip)} title="Bloquear permanentemente" data-testid={`perm-block-${b.ip}`}>
                        <Ban className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => handleUnblock(b.ip)} title="Desbloquear" data-testid={`unblock-${b.ip}`}>
                      <Unlock className="w-4 h-4 text-green-500" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Maintenance Mode */}
      <div className="bg-card border border-border p-4">
        <h2 className="font-semibold mb-3 flex items-center gap-2"><Lock className="w-4 h-4" /> Modo Mantenimiento</h2>
        <div className="flex items-center gap-4">
          <span className={`text-sm ${maintenance?.enabled ? 'text-red-500' : 'text-green-500'}`}>
            {maintenance?.enabled ? 'ACTIVADO' : 'Desactivado'}
          </span>
          {!maintenance?.enabled && (
            <div className="flex gap-2">
              <Input placeholder="Mensaje" value={maintenanceForm.message} onChange={e => setMaintenanceForm({...maintenanceForm, message: e.target.value})} className="w-64" />
              <Input type="number" value={maintenanceForm.hours} onChange={e => setMaintenanceForm({...maintenanceForm, hours: parseInt(e.target.value)})} className="w-20" min={1} />
              <span className="text-sm text-muted-foreground self-center">horas</span>
            </div>
          )}
          <Button size="sm" variant={maintenance?.enabled ? 'outline' : 'destructive'} onClick={handleMaintenanceToggle}>
            {maintenance?.enabled ? 'Desactivar' : 'Activar'}
          </Button>
        </div>
      </div>

      {/* Block IP Modal */}
      {showBlockModal && (
        <DraggableModal title="Bloquear IP" onClose={() => setShowBlockModal(false)} width="440px">
          <div className="space-y-3 p-4">
            <Input placeholder="Dirección IP" value={blockForm.ip} onChange={e => setBlockForm({...blockForm, ip: e.target.value})} data-testid="block-ip-input" />
            <Input placeholder="Motivo (opcional)" value={blockForm.reason} onChange={e => setBlockForm({...blockForm, reason: e.target.value})} />
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={blockForm.permanent} onChange={e => setBlockForm({...blockForm, permanent: e.target.checked})} />
              <Ban className="w-3.5 h-3.5 text-red-500" /> Bloqueo permanente
            </label>
            <Button onClick={handleBlockIp} className="w-full" data-testid="confirm-block-btn">Bloquear</Button>
          </div>
        </DraggableModal>
      )}

      {/* Settings Modal */}
      {showSettingsModal && (
        <DraggableModal title="Ajustes de Seguridad" onClose={() => setShowSettingsModal(false)} width="400px">
          <div className="space-y-4 p-4">
            <div>
              <label className="text-sm font-medium block mb-1">Umbral de bloqueo permanente automático</label>
              <p className="text-xs text-muted-foreground mb-2">
                Número de bloqueos temporales antes de que una IP se bloquee automáticamente de forma permanente.
              </p>
              <Input type="number" min={2} max={20} value={threshold} onChange={e => setThreshold(parseInt(e.target.value) || 3)} data-testid="threshold-input" />
            </div>
            <Button onClick={handleSaveSettings} className="w-full" data-testid="save-settings-btn">Guardar</Button>
          </div>
        </DraggableModal>
      )}
    </div>
  );
};

export default AdminSecurity;
